import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Plus } from "lucide-react";
import { toast } from "sonner";

import { AlphabetIndex, letterOf } from "@/components/cinch/AlphabetIndex";
import {
  ActiveFilterPill,
  FilterBar,
  FilterChip,
  FilterClearAll,
  FilterSearch,
} from "@/components/cinch/FilterBar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useCan } from "@/lib/auth";

import {
  type GlossaryTerm,
  type TermKind,
  type TermStatus,
  useApproveTerm,
  useGlossary,
} from "../api";
import { ApiError } from "@/lib/api";
import { TermCard } from "../components/TermCard";
import { TermEditDialog } from "../components/TermEditDialog";

const KIND_OPTIONS: { key: TermKind; label: string }[] = [
  { key: "concept", label: "Concept" },
  { key: "metric", label: "Metric" },
  { key: "calculation", label: "Calculation" },
  { key: "query", label: "Query" },
];

const STATUS_OPTIONS: { key: TermStatus; label: string }[] = [
  { key: "in_review", label: "In Review" },
  { key: "approved", label: "Approved" },
];

const words = (s: string) => s.split(/[^a-z0-9]+/).filter(Boolean);

/**
 * Relevance score of a term against a lower-cased query. 0 = no match.
 *
 * Weighted so title (the term name) dominates, then aliases, tags, and finally
 * the body (definition / equation / query). Within each field, an exact match
 * beats a prefix, which beats a word-start, which beats a mid-string hit — so
 * "churn" surfaces the term *named* Churn before terms that merely mention it.
 */
function scoreTerm(t: GlossaryTerm, q: string): number {
  const name = t.term.toLowerCase();
  const aliases = (t.aliases ?? []).map((a) => a.toLowerCase());
  const tags = t.tags.map((x) => x.toLowerCase());
  const body = [t.definition, t.equation ?? "", t.query ?? ""].join(" ").toLowerCase();

  let score = 0;

  // Title
  if (name === q) score += 1000;
  else if (name.startsWith(q)) score += 500;
  else if (words(name).some((w) => w.startsWith(q))) score += 300;
  else if (name.includes(q)) score += 150;

  // Aliases
  if (aliases.some((a) => a === q)) score += 220;
  else if (aliases.some((a) => a.startsWith(q) || words(a).some((w) => w.startsWith(q)))) score += 120;
  else if (aliases.some((a) => a.includes(q))) score += 60;

  // Tags
  if (tags.some((x) => x === q)) score += 60;
  else if (tags.some((x) => x.includes(q))) score += 35;

  // Body — lowest weight, so a definition mention never outranks a title hit.
  if (body.includes(q)) score += 10;

  // Multi-word fallback: no single field held the whole phrase, but every word
  // appears somewhere → include it, boosted for each word found in the title.
  if (score === 0) {
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length > 1) {
      const hay = `${name} ${aliases.join(" ")} ${tags.join(" ")} ${body}`;
      if (tokens.every((tok) => hay.includes(tok))) {
        score = 5 + tokens.filter((tok) => name.includes(tok)).length * 40;
      }
    }
  }

  return score;
}

/** Nearest scrollable ancestor (the app's <main> scrolls, not the window). */
function scrollParent(el: HTMLElement | null): HTMLElement | null {
  let n = el?.parentElement ?? null;
  while (n) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight) return n;
    n = n.parentElement;
  }
  return null;
}

export function GlossaryPage() {
  const { data, isLoading, isError, error } = useGlossary();
  const isAdmin = useCan("glossary.admin");

  const approveTerm = useApproveTerm();

  const [q, setQ] = useState("");
  const [kinds, setKinds] = useState<TermKind[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<TermStatus[]>([]);
  const [highlighted, setHighlighted] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<GlossaryTerm | null>(null);
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const terms = useMemo(() => data?.items ?? [], [data]);
  const allTags = data?.tags ?? [];

  const bySlug = useMemo(
    () => new Map(terms.map((t) => [t.slug, t] as const)),
    [terms],
  );
  const resolve = useCallback((slug: string) => bySlug.get(slug), [bySlug]);

  const toggleKind = (k: TermKind) =>
    setKinds((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));
  const toggleTag = (t: string) =>
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const toggleStatus = (s: TermStatus) =>
    setStatuses((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));

  const clearAll = () => {
    setQ("");
    setKinds([]);
    setTags([]);
    setStatuses([]);
  };
  const hasFilters =
    q.length > 0 || kinds.length > 0 || tags.length > 0 || statuses.length > 0;

  const approve = async (term: GlossaryTerm) => {
    try {
      await approveTerm.mutateAsync(term.id);
      toast.success(`Approved "${term.term}"`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Approve failed");
    }
  };

  // One flat list. A term matches if it carries ANY selected tag / kind. With a
  // search query it's ranked by relevance (title-weighted); otherwise A–Z.
  const query = q.trim().toLowerCase();
  const searching = query.length > 0;
  const filtered = useMemo(() => {
    const base = terms.filter((t) => {
      if (kinds.length && !t.kinds.some((k) => kinds.includes(k))) return false;
      if (tags.length && !t.tags.some((tag) => tags.includes(tag))) return false;
      if (statuses.length && !statuses.includes(t.status)) return false;
      return true;
    });
    if (!query) return [...base].sort((a, b) => a.term.localeCompare(b.term));
    return base
      .map((t) => ({ t, s: scoreTerm(t, query) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s || a.t.term.localeCompare(b.t.term))
      .map((x) => x.t);
  }, [terms, query, kinds, tags, statuses]);

  // A–Z rail: which letters have a (filtered) term, and the first term per
  // letter to scroll to.
  const availableLetters = useMemo(
    () => new Set(filtered.map((t) => letterOf(t.term))),
    [filtered],
  );
  const firstSlugByLetter = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of filtered) {
      const l = letterOf(t.term);
      if (!m.has(l)) m.set(l, t.slug);
    }
    return m;
  }, [filtered]);

  const jumpToLetter = useCallback(
    (letter: string) => {
      const slug = firstSlugByLetter.get(letter);
      if (slug) {
        // Instant, not smooth: a smooth animation across the full list gets
        // dropped by the browser on a container this tall (same reason the
        // cross-link jump is instant).
        document.getElementById(`term-${slug}`)?.scrollIntoView({ behavior: "auto", block: "start" });
        // Light up the target letter immediately — a single instant jump
        // doesn't reliably fire the scroll listener that otherwise tracks it.
        setActiveLetter(letter);
      }
    },
    [firstSlugByLetter],
  );

  // Keep the rail's active letter synced to what's scrolled to the top.
  useEffect(() => {
    const list = listRef.current;
    const scroller = scrollParent(list);
    if (!list || !scroller) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      // Match the cards' scroll-mt-24 (96px): a card jumped to the top rests
      // 96px down, so the threshold must clear that or the *previous* letter
      // (peeking into the margin) would read as active.
      const top = scroller.getBoundingClientRect().top + 104;
      const rows = list.querySelectorAll<HTMLElement>("[data-letter]");
      let current: string | null = rows[0]?.dataset.letter ?? null;
      for (const row of rows) {
        if (row.getBoundingClientRect().top <= top) current = row.dataset.letter ?? current;
        else break;
      }
      setActiveLetter(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [filtered]);

  // Jump to a term by slug — clear filters so the target is visible, flash it.
  // Clearing filters re-renders the full list, so poll a few animation frames
  // until the target node exists, then jump (instant; a long smooth scroll gets
  // dropped by that same-frame reflow).
  const navigate = useCallback((slug: string) => {
    clearAll();
    setHighlighted(slug);
    let tries = 0;
    const tryScroll = () => {
      const el = document.getElementById(`term-${slug}`);
      if (el) {
        el.scrollIntoView({ behavior: "auto", block: "start" });
        requestAnimationFrame(() =>
          document.getElementById(`term-${slug}`)?.scrollIntoView({ block: "start" }),
        );
      } else if (tries++ < 30) {
        requestAnimationFrame(tryScroll);
      }
    };
    requestAnimationFrame(tryScroll);
    window.setTimeout(() => setHighlighted(null), 2400);
  }, []);

  // Deep-link support: /glossary#<slug> jumps to that term once the list has
  // loaded (other features — e.g. Budget vs Actual category tooltips — link
  // straight to a definition).
  const hashJumped = useRef(false);
  useEffect(() => {
    if (hashJumped.current || terms.length === 0) return;
    const slug = window.location.hash.replace(/^#/, "");
    if (slug && bySlug.has(slug)) {
      hashJumped.current = true;
      navigate(slug);
    }
  }, [terms, bySlug, navigate]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (t: GlossaryTerm) => {
    setEditing(t);
    setDialogOpen(true);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 pb-16">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Glossary</h1>
          <p className="text-sm text-muted-foreground">
            What each term means, how it's calculated, and the exact query behind
            it — so everyone (and every tool) works from the same numbers.
          </p>
        </div>
        {isAdmin ? (
          <Button onClick={openCreate} className="shrink-0">
            <Plus className="mr-1.5 h-4 w-4" />
            Add term
          </Button>
        ) : null}
      </header>

      <div className="space-y-3">
        <FilterBar>
          <FilterSearch value={q} onChange={setQ} placeholder="Search terms & definitions" />

          <FilterChip label="Kind" count={kinds.length}>
            <div className="space-y-2">
              {KIND_OPTIONS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={kinds.includes(key)} onCheckedChange={() => toggleKind(key)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </FilterChip>

          {allTags.length > 0 ? (
            <FilterChip label="Tag" count={tags.length}>
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {allTags.map((t) => (
                  <label key={t} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={tags.includes(t)} onCheckedChange={() => toggleTag(t)} />
                    <span>{t}</span>
                  </label>
                ))}
              </div>
            </FilterChip>
          ) : null}

          <FilterChip label="Status" count={statuses.length}>
            <div className="space-y-2">
              {STATUS_OPTIONS.map(({ key, label }) => (
                <label key={key} className="flex items-center gap-2 text-sm">
                  <Checkbox checked={statuses.includes(key)} onCheckedChange={() => toggleStatus(key)} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </FilterChip>

          <div className="flex-1" />
          {!isLoading && !isError ? (
            <span className="text-xs text-muted-foreground">
              {filtered.length} of {terms.length}
            </span>
          ) : null}
          {hasFilters ? <FilterClearAll onClick={clearAll} /> : null}
        </FilterBar>

        {kinds.length || tags.length || statuses.length ? (
          <div className="flex flex-wrap gap-2">
            {kinds.map((k) => (
              <ActiveFilterPill key={`k-${k}`} label={`Kind: ${k}`} onRemove={() => toggleKind(k)} />
            ))}
            {tags.map((t) => (
              <ActiveFilterPill key={`t-${t}`} label={`Tag: ${t}`} onRemove={() => toggleTag(t)} />
            ))}
            {statuses.map((s) => (
              <ActiveFilterPill
                key={`s-${s}`}
                label={`Status: ${s === "in_review" ? "In Review" : "Approved"}`}
                onRemove={() => toggleStatus(s)}
              />
            ))}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      ) : isError ? (
        <Alert variant="danger">
          <AlertTitle>Couldn't load the glossary</AlertTitle>
          <AlertDescription>{(error as Error)?.message ?? "Try again."}</AlertDescription>
        </Alert>
      ) : filtered.length === 0 ? (
        <Alert variant="info">
          <AlertTitle>No matching terms</AlertTitle>
          <AlertDescription>
            {hasFilters ? "Adjust or clear the filters above." : "The glossary is empty."}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex items-start gap-3">
          <div ref={listRef} className="min-w-0 flex-1 space-y-4">
            {filtered.map((t) => (
              <div key={t.id} data-letter={letterOf(t.term)}>
                <TermCard
                  term={t}
                  resolve={resolve}
                  onNavigate={navigate}
                  onTag={(tag) => setTags((cur) => (cur.includes(tag) ? cur : [...cur, tag]))}
                  onEdit={isAdmin ? openEdit : undefined}
                  onApprove={isAdmin ? approve : undefined}
                  highlighted={highlighted === t.slug}
                />
              </div>
            ))}
          </div>
          {/* The A–Z rail only makes sense for the alphabetical list — hide it
              while searching, when results are ranked by relevance. */}
          {searching ? null : (
            <AlphabetIndex
              available={availableLetters}
              active={activeLetter}
              onSelect={jumpToLetter}
              className="sticky top-2 hidden self-start rounded-md border bg-card/80 p-1 backdrop-blur md:flex"
            />
          )}
        </div>
      )}

      {isAdmin ? (
        <TermEditDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          term={editing}
          allTerms={terms}
          allTags={allTags}
        />
      ) : null}
    </div>
  );
}
